import type { MarketResponseDto } from "Infrastructure/Api/Api";
import { ApiCallError } from "Infrastructure/Api/ApiUtils";
import { useMarketNavigate } from "Infrastructure/Routes/Hooks/useMarketNavigate";
import { useMarketByUrlPathQuery } from "Market/Api/Query/useMarketByUrlPathQuery";
import { MarketPaths } from "Market/Routes/Routing";
import { getMarketFromPath } from "Market/Utilities/getMarketFromPath";
import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useState,
} from "react";

interface MarketContextType {
	market?: MarketResponseDto | null;
	isLoading: boolean;
}

const MarketContext = createContext<MarketContextType | undefined>(undefined);

export const useMarket = () => {
	const context = useContext(MarketContext);
	if (!context) {
		throw new Error("useMarket must be used within a MarketProvider");
	}
	return context;
};

export const MarketProvider = ({ children }: { children: ReactNode }) => {
	const urlPath = getMarketFromPath();
	const marketNavigate = useMarketNavigate();
	const {
		data,
		error,
		isLoading: isDataLoading,
		refetch,
	} = useMarketByUrlPathQuery(urlPath);

	const [market, setMarket] = useState<MarketResponseDto | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const marketApiCallError = error instanceof ApiCallError ? error : undefined;

	useEffect(() => {
		setIsLoading(isDataLoading);
	}, [isDataLoading]);

	useEffect(() => {
		if (urlPath && data && !marketApiCallError && !market) {
			setMarket(data);

			return;
		}

		if (!data && !market) {
			refetch();
		}
	}, [data, market]);

	if (urlPath && marketApiCallError?.data.status === 404) {
		marketNavigate(MarketPaths.Index.path, "");

		return null;
	}

	if ((urlPath && !data) || isDataLoading) {
		return null;
	}

	return (
		<MarketContext.Provider value={{ market, isLoading }}>
			{children}
		</MarketContext.Provider>
	);
};
